import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface EventSystemStatus {
  system_status: string;
  event_queue: {
    pending: number;
    processing: number;
    failed_today: number;
    completed_today: number;
  };
  configuration: {
    active_flows: number;
    active_templates: number;
  };
  last_checked: string;
}

export interface EventFlow {
  id: string;
  name: string;
  description: string;
  from_status: string | null; // NULL for creation events
  to_status: string;
  from_status_info?: { display_name: string; color: string };
  to_status_info?: { display_name: string; color: string };
  template_category: string;
  channels: string[];
  recipient_config: {
    types: string[];
    custom_emails?: string[];
  };
  is_active: boolean;
  delay_minutes: number;
  conditions: Record<string, any>;
  priority: number;
  org_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CommunicationTemplate {
  id: string;
  category: string;
  name: string;
  description: string;
  subject_template: string;
  content_template: string;
  supported_channels: string[];
  variables: Record<string, any>;
  is_active: boolean;
  version: number;
  org_id: string | null;
  created_at: string;
  updated_at: string;
}

// Get event system status
export const useEventSystemStatus = () => {
  return useQuery({
    queryKey: ['event-system-status'],
    queryFn: async (): Promise<EventSystemStatus> => {
      console.log('[EVENT-SYSTEM] Fetching system status');
      const { data, error } = await supabase.rpc('get_event_system_status');
      
      if (error) {
        console.error('[EVENT-SYSTEM] Error fetching status:', error);
        throw error;
      }
      
      console.log('[EVENT-SYSTEM] Status:', data);
      return data as unknown as EventSystemStatus;
    },
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 60, // 1 minute
  });
};

// Get event flows (open transitions architecture)
export const useEventFlows = () => {
  return useQuery({
    queryKey: ['event-flows'],
    queryFn: async (): Promise<EventFlow[]> => {
      console.log('[EVENT-SYSTEM] Fetching event flows');
      const { data, error } = await supabase
        .from('event_flows')
        .select('*')
        .order('priority', { ascending: true });
      
      if (error) {
        console.error('[EVENT-SYSTEM] Error fetching event flows:', error);
        throw error;
      }
      
      // Fetch status info separately
      const enrichedData = await Promise.all((data || []).map(async (flow: any) => {
        const statusPromises = [];
        
        if (flow.from_status) {
          statusPromises.push(
            supabase
              .from('booking_statuses')
              .select('display_name, color')
              .eq('status_key', flow.from_status)
              .maybeSingle()
              .then(({ data }) => data)
          );
        } else {
          statusPromises.push(Promise.resolve(null));
        }
        
        statusPromises.push(
          supabase
            .from('booking_statuses')
            .select('display_name, color')
            .eq('status_key', flow.to_status)
            .maybeSingle()
            .then(({ data }) => data)
        );
        
        const [fromInfo, toInfo] = await Promise.all(statusPromises);
        
        return {
          id: flow.id,
          name: flow.name,
          description: flow.description,
          from_status: flow.from_status,
          to_status: flow.to_status,
          from_status_info: fromInfo,
          to_status_info: toInfo,
          template_category: flow.template_category,
          channels: flow.channels,
          recipient_config: flow.recipient_config as { types: string[]; custom_emails?: string[] },
          is_active: flow.is_active,
          delay_minutes: flow.delay_minutes,
          conditions: flow.conditions as Record<string, any>,
          priority: flow.priority,
          org_id: flow.org_id,
          created_at: flow.created_at,
          updated_at: flow.updated_at
        };
      }));
      
      console.log('[EVENT-SYSTEM] Event flows:', enrichedData);
      return enrichedData as EventFlow[];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Get communication templates
export const useCommunicationTemplates = () => {
  return useQuery({
    queryKey: ['communication-templates'],
    queryFn: async (): Promise<CommunicationTemplate[]> => {
      console.log('[EVENT-SYSTEM] Fetching communication templates');
      const { data, error } = await supabase
        .from('communication_templates')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true });
      
      if (error) {
        console.error('[EVENT-SYSTEM] Error fetching templates:', error);
        throw error;
      }
      
      console.log('[EVENT-SYSTEM] Templates:', data);
      return (data || []).map(template => ({
        ...template,
        variables: template.variables as Record<string, any>
      }));
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Trigger manual event processing
export const useManualEventProcessing = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      console.log('[EVENT-SYSTEM] Triggering manual event processing');
      
      // First trigger database function
      const { data: dbResult, error: dbError } = await supabase.rpc('trigger_manual_event_processing');
      
      if (dbError) {
        console.error('[EVENT-SYSTEM] Database trigger error:', dbError);
        throw dbError;
      }
      
      console.log('[EVENT-SYSTEM] Database trigger result:', dbResult);
      
      // Then trigger the edge function
      const { data: edgeResult, error: edgeError } = await supabase.functions.invoke('process-event-queue');
      
      if (edgeError) {
        console.error('[EVENT-SYSTEM] Edge function error:', edgeError);
        // Don't throw here as DB trigger was successful
      }
      
      console.log('[EVENT-SYSTEM] Edge function result:', edgeResult);
      
      return {
        database: dbResult,
        edgeFunction: edgeResult
      };
    },
    onSuccess: () => {
      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['event-system-status'] });
      console.log('[EVENT-SYSTEM] Manual processing triggered successfully');
    },
    onError: (error) => {
      console.error('[EVENT-SYSTEM] Manual processing failed:', error);
    }
  });
};

// Test the event system by creating a test booking status change
export const useTestEventSystem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: { bookingId: string; newStatus: string; notes?: string }) => {
      console.log('[EVENT-SYSTEM] Testing event system with status change:', params);
      
      const { data, error } = await supabase.rpc('update_booking_status', {
        p_booking_id: params.bookingId,
        p_new_status: params.newStatus,
        p_notes: params.notes || 'Event system test'
      });
      
      if (error) {
        console.error('[EVENT-SYSTEM] Test status update error:', error);
        throw error;
      }
      
      console.log('[EVENT-SYSTEM] Test status update result:', data);
      return data;
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['event-system-status'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      console.log('[EVENT-SYSTEM] Test completed successfully');
    },
    onError: (error) => {
      console.error('[EVENT-SYSTEM] Test failed:', error);
    }
  });
};

// Force retrigger events for testing (bypasses status change check)
export const useForceRetriggerEvents = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: { 
      bookingId: string; 
      fromStatus: string; 
      toStatus: string; 
      notes?: string; 
      updatedBy?: string 
    }) => {
      console.log('[EVENT-SYSTEM] Force retriggering events:', params);
      
      const { data, error } = await supabase.rpc('force_retrigger_events', {
        p_booking_id: params.bookingId,
        p_from_status: params.fromStatus,
        p_to_status: params.toStatus,
        p_updated_by: params.updatedBy || null,
        p_notes: params.notes || 'Force retrigger for testing'
      });
      
      if (error) {
        console.error('[EVENT-SYSTEM] Force retrigger error:', error);
        throw error;
      }
      
      console.log('[EVENT-SYSTEM] Force retrigger result:', data);
      return data;
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['event-system-status'] });
      queryClient.invalidateQueries({ queryKey: ['event-queue'] });
      console.log('[EVENT-SYSTEM] Force retrigger completed successfully');
    },
    onError: (error) => {
      console.error('[EVENT-SYSTEM] Force retrigger failed:', error);
    }
  });
};